import { Box, Button, FormHelperText, makeStyles, Typography } from '@material-ui/core';
import { useNamedItemMap } from '../../../useNamedItemMap';
import AddIcon from '@material-ui/icons/Add';
import LambdaIcon from '../../../../assets/aws/lambda.svg';
import SqsIcon from '../../../../assets/aws/sqs.svg';
import type { ValkyrieLambda } from 'config-editor-common';
import { ValkyrieQueueConsumerItemEditor } from './ValkyrieQueueConsumerItemEditor';

const SQS_PINK = '#E7157B';
const LAMBDA_ORANGE = '#FF9900';

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(2),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  icon: {
    width: '1.5rem',
    height: '1.5rem',
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  addAnotherButton: {
    marginTop: theme.spacing(2),
  },
}));

interface ValkyrieQueueConsumerListEditorProps {
  lambdas: Record<string, ValkyrieLambda>;
  onChange: (lambdas: Record<string, ValkyrieLambda>) => void;
  onRename?: (oldName: string, newName: string) => void;
  /** Available SQS queue names */
  queueNames?: string[];
}

const DEFAULT_QUEUE_CONSUMER: ValkyrieLambda = {
  lambda_handler: '',
  timeout: 300,
  memory_size: 256,
};

export const ValkyrieQueueConsumerListEditor = ({
  lambdas,
  onChange,
  onRename,
  queueNames = [],
}: ValkyrieQueueConsumerListEditorProps) => {
  const classes = useStyles();

  const { add, update, remove, rename } = useNamedItemMap<ValkyrieLambda>(
    lambdas,
    onChange,
    () => ({ ...DEFAULT_QUEUE_CONSUMER }),
    { namePrefix: 'new_consumer', onRenamed: onRename },
  );

  const canAddConsumer = queueNames.length > 0;

  return (
    <div className={classes.section}>
      <Box className={classes.header}>
        <div className={classes.titleContainer}>
          <img src={LambdaIcon} alt="Lambda" className={classes.icon} />
          <img src={SqsIcon} alt="SQS" className={classes.icon} />
          <Typography variant="h6">Queue Consumer Lambdas</Typography>
        </div>
        <Button
          variant="contained"
          color="primary"
          onClick={add}
          disabled={!canAddConsumer}
          startIcon={<AddIcon />}
          style={
            canAddConsumer
              ? {
                  background: `linear-gradient(135deg, ${SQS_PINK}, ${LAMBDA_ORANGE})`,
                  color: '#FFFFFF',
                }
              : undefined
          }
        >
          Add Consumer
        </Button>
      </Box>

      {!canAddConsumer && (
        <FormHelperText style={{ marginBottom: 16 }}>
          Add at least one SQS queue in the SQS Queues tab to create queue consumer lambdas.
        </FormHelperText>
      )}

      {Object.keys(lambdas).length === 0 ? (
        <div className={classes.emptyState}>
          <Typography variant="body1">No queue consumer lambdas configured</Typography>
          <Typography variant="body2" style={{ marginTop: 8 }}>
            {canAddConsumer
              ? 'Add a queue consumer lambda to process messages from your SQS queues.'
              : 'Add at least one SQS queue in the SQS Queues tab to create queue consumer lambdas.'}
          </Typography>
          {canAddConsumer && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={add}
              style={{
                marginTop: 16,
                background: `linear-gradient(135deg, ${SQS_PINK}, ${LAMBDA_ORANGE})`,
                color: '#FFFFFF',
              }}
            >
              Add First Consumer
            </Button>
          )}
        </div>
      ) : (
        <>
          {Object.entries(lambdas).map(([lambdaName, lambda]) => (
            <ValkyrieQueueConsumerItemEditor
              key={lambdaName}
              name={lambdaName}
              lambda={lambda}
              onUpdate={update}
              onRename={rename}
              onDelete={remove}
              queueNames={queueNames}
            />
          ))}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={add}
            disabled={!canAddConsumer}
            className={classes.addAnotherButton}
          >
            Add another consumer
          </Button>
        </>
      )}
    </div>
  );
};
